/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty, truncate } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  ICarbonScopeDetailWithLoaderContent,
  IDetailComponentProps,
  ICarbonScopeOneData,
  ICarbonScopeOneStackedData,
  IAny
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card, Icon, Tooltip } from '@scuf/common';
import CarbonScopeOneStackedDetailsComponent from './CarbonScopeOneStackedDetailsComponent';
import CarbonScopeOneDetailsComponent from './CarbonScopeOneDetailsComponent';
import { ApiContext } from '../../../../context/ApiContext';
import {
  cardHeaderPremiumCheck,
  spanHeaderPremiumCheck,
  UserContext
} from '../TotalEmissionDetailWidget/TotalEmissionDetailWidgetSeparateAPI';
import CarbonScopeOneStackedDetailsComponentSeparateAPI from './CarbonScopeOneStackedDetailsComponentSeparateAPI';
import { EnergyUsageDataSourceType } from '../../../shared/Enums';

const CarbonScopeOneDetailWithLoaderContent = (
  props: ICarbonScopeDetailWithLoaderContent
) => {
  const d = descriptors.energyManagement;
  const e = descriptors.DetailedView;
  const [t] = useTranslation();
  const {
    data,
    onRefresh,
    carbonAllSiteMonthData,
    requestContext,
    checkAllSitesData,
    isSite
  } = props;
  const emaasContext = useContext(ApiContext);
  const { toggleFeatureScopeOne } = emaasContext!;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        {toggleFeatureScopeOne ? (
          <CarbonScopeOneStackedDetailsComponentSeparateAPI
            data={data as ICarbonScopeOneStackedData}
            trendChartCategory={t(e.SCOPE1)}
            carbonAllSiteMonthData={carbonAllSiteMonthData}
            forSummary={props.forSummary}
            forGraph={props.forGraph}
            dataSourceType={props.dataSourceType}
            checkAllSitesData={checkAllSitesData}
            isSite={isSite}
          />
        ) : (
          <UserContext.Provider value={requestContext}>
            <CarbonScopeOneDetailsComponent
              data={data as ICarbonScopeOneData}
              trendChartCategory={t(e.SCOPE1)}
              forSummary={props.forSummary}
              forGraph={props.forGraph}
              dataSourceType={props.dataSourceType}
              isSite={isSite}
            />
          </UserContext.Provider>
        )}
      </div>
    </div>
  );
};

const CarbonScopeOneDetailWithLoader = withLoadingStatus(
  withErrorStatus(CarbonScopeOneDetailWithLoaderContent)
);

const CarbonScopeOneDetailWidgetSeparateAPI = (
  props: IDetailComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.DetailedView;
  const e = descriptors.energyManagement;

  const emaasContext = useContext(ApiContext);
  const { toggleFeatureScopeOne } = emaasContext!;

  const {
    dataConnector,
    isSite,
    dataConnectorForGraph,
    dataConnectorForTable,
    requestContext,
    dataSourceType
  } = props;

  const apiContext = useContext(ApiContext);
  const { cemsEnableCarbonSourceSelect } = apiContext;

  const onRefresh = () => dataConnector?.actions?.getAll();

  const onRefreshPerf = () => dataConnectorForGraph?.actions?.getAll();

  const { all, loading, error, status } = dataConnector?.data;

  const {
    all: allPerf,
    loading: loadingPerf,
    error: errorPerf,
    status: statusPerf
  } = dataConnectorForGraph?.data;

  const {
    all: allTable,
    loading: loadingTable,
    error: errorTable,
    status: statusTable
  } = dataConnectorForTable?.data;

  const carbonScopeOneData = (all as unknown) as ICarbonScopeOneData;

  const isDataAvailable = carbonScopeOneData ? true : false;

  const graphData = (allPerf as unknown) as ICarbonScopeOneData;
  let isDataAvailableForGraph: IAny;
  if (toggleFeatureScopeOne) {
    isDataAvailableForGraph = graphData?.monthWiseEmissionByFuelType?.length
      ? true
      : false;
  } else {
    isDataAvailableForGraph = graphData?.monthWiseAggregations?.length
      ? true
      : false;
  }

  const tableData = (allTable as unknown) as ICarbonScopeOneData;

  const isDataAvailableForTable = tableData?.breakdownBySite?.length
    ? true
    : false;

  const [isLoading, setIsLoading] = useState<boolean>(loadingPerf);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonScopeOneData);

  const [isLoadingPerf, setIsLoadingPerf] = useState<boolean>(loadingPerf);
  const [statusCodePerf, setStatusCodePerf] = useState<number | null>(0);
  const [errorInfoPerf, setErrorInfoPerf] = useState(errorPerf);
  const [dataPerf, setDataPerf] = useState({} as ICarbonScopeOneData);

  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(loadingPerf);
  const [statusCodeTable, setStatusCodeTable] = useState<number | null>(0);
  const [errorInfoTable, setErrorInfoTable] = useState(errorTable);
  const [dataTable, setDataTable] = useState({} as ICarbonScopeOneData);

  const [carbonAllSiteMonthData, setAllSiteMonthData] = useState(true);
  const [checkAllSitesData, setCheckAllSitesData] = useState<boolean>(true);

  const checkIfAllSiteHasCompleteData = (data: any) => {
    return (
      isSite ||
      (data &&
        data.breakdownBySite &&
        data.breakdownBySite.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      isSite ||
      (data &&
        data.monthWiseEmissionByFuelType &&
        data.monthWiseEmissionByFuelType.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  // Only Applicable for Portfolio
  const checkIfAllSitesHasPreviousPeriod = (data: any) => {
    return (
      isSite ||
      (data &&
        data.breakdownBySite &&
        data.breakdownBySite.every(
          (item: {
            totalPreviousPeriodEmission: number | null;
            totalActualEmission: number | null;
          }) =>
            item.totalActualEmission !== 0 &&
            item.totalPreviousPeriodEmission !== 0 &&
            item.totalActualEmission !== null &&
            item.totalPreviousPeriodEmission !== null
        ))
    );
  };

  // Only Applicable for Portfolio
  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      isSite ||
      (data &&
        data.monthWiseEmissionByFuelType &&
        data.monthWiseEmissionByFuelType.every(
          (item: {
            totalPreviousPeriodEmission: number | null;
            totalActualEmission: number | null;
          }) =>
            item.totalPreviousPeriodEmission !== 0 &&
            item.totalPreviousPeriodEmission !== null &&
            item.totalActualEmission !== 0 &&
            item.totalActualEmission !== null
        ))
    );
  };

  useEffect(() => {
    if (isDataAvailableForTable || errorTable || statusTable || !loadingTable) {
      let tempData: any = tableData;
      setCheckAllSitesData(
        checkIfAllSitesHasPreviousPeriod(tempData) &&
          checkIfAllSiteHasCompleteData(tempData)
      );
    }
  }, [isDataAvailableForTable, isLoadingTable, statusCodeTable]);

  useEffect(() => {
    setIsLoadingPerf(loadingPerf);
    if (isDataAvailableForGraph || errorPerf || statusPerf || !loadingPerf) {
      if (isDataAvailableForGraph) {
        let tempData: IAny = JSON.parse(JSON.stringify(graphData));
        setAllSiteMonthData(
          checkIfAllMonthsHasPreviousPeriod(tempData) &&
            checkIfAllMonthHasCompleteData(tempData)
        );
        setDataPerf(tempData);
      }
      setErrorInfoPerf(errorPerf);
      setStatusCodePerf(statusPerf);
    }
  }, [isDataAvailableForGraph, statusPerf, loadingPerf]);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(carbonScopeOneData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header className={cardHeaderPremiumCheck(requestContext)}>
          <span className={spanHeaderPremiumCheck(requestContext)}>
            {t(d.SCOPEONETITLE)}
          </span>
          <Tooltip
            element={
              <Icon
                root="common"
                name="badge-info"
                exactSize="0.875rem"
                className="emaas-summary-card-header-info-icon"
              />
            }
            event="hover"
            // content={t(d.SCOPEONETOOLTIP)}
            content={
              requestContext?.context.subscriptionType === 'BASIC_SUBSCRIPTION'
                ? t(d.SCOPEONETOOLTIP)
                : cemsEnableCarbonSourceSelect
                ? dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                  ? t(d.SCOPEONETOOLTIPUTILITY)
                  : dataSourceType ===
                    EnergyUsageDataSourceType.UTILITY_INTERVAL
                  ? t(d.SCOPEONETOOLTIPINTERVAL)
                  : t(d.SCOPEONETOOLTIPLIVE)
                : t(d.SCOPEONETOOLTIP)
            }
            position="right center"
            size="small"
          />
        </Card.Header>

        {statusCode == 204 || statusCodePerf == 204 ? (
          <Card.Content className="detail-summary-card detail-view">
            <CarbonScopeOneDetailWithLoader
              loading={isLoading}
              error={errorInfo}
              statusCode={statusCode}
              onRefresh={onRefresh}
              data={data}
              loadingText={t(e.LOADING)}
              title={t(e.CONNECTIONERRORTITLE)}
              noDataMessage={t(e.NODATAAVAILABLE)}
              connectionErrorMessage={t(e.CONNECTIONERROR)}
              carbonAllSiteMonthData={carbonAllSiteMonthData}
              checkAllSitesData={checkAllSitesData}
            />
          </Card.Content>
        ) : (
          <>
            <Card.Content className="detail-summary-card detail-view">
              <CarbonScopeOneDetailWithLoader
                loading={isLoading}
                error={errorInfo}
                statusCode={statusCode}
                onRefresh={onRefresh}
                data={data}
                loadingText={t(e.LOADING)}
                title={t(e.CONNECTIONERRORTITLE)}
                noDataMessage={t(e.NODATAAVAILABLE)}
                connectionErrorMessage={t(e.CONNECTIONERROR)}
                requestContext={requestContext}
                carbonAllSiteMonthData={carbonAllSiteMonthData}
                forSummary={true}
                dataSourceType={props.dataSourceType}
                checkAllSitesData={checkAllSitesData}
              />
            </Card.Content>
            <Card.Content className="detail-summary-card detail-view">
              <CarbonScopeOneDetailWithLoader
                loading={isLoadingPerf}
                error={errorInfoPerf}
                statusCode={statusCodePerf}
                onRefresh={onRefreshPerf}
                data={dataPerf}
                loadingText={t(e.LOADING)}
                title={t(e.CONNECTIONERRORTITLE)}
                noDataMessage={t(e.NODATAAVAILABLE)}
                connectionErrorMessage={t(e.CONNECTIONERROR)}
                requestContext={requestContext}
                carbonAllSiteMonthData={carbonAllSiteMonthData}
                forGraph={true}
                dataSourceType={props.dataSourceType}
                isSite={isSite}
              />
            </Card.Content>
          </>
        )}
      </Card>
    </>
  );
};
export default CarbonScopeOneDetailWidgetSeparateAPI;
